import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Sparkles, ShieldCheck, Layers, GitBranch, Activity, Boxes, Container } from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const ICONS = {
  prospect: GitBranch, research: Layers, personalization: Sparkles, safety: ShieldCheck,
  content: Bot, engagement: Activity, crm: Boxes, analytics: Container,
};

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);

  const loadAll = async () => {
    const [a, r] = await Promise.all([api.get("/agents"), api.get("/agents/runs?limit=15")]);
    setAgents(a.data.agents || []);
    setRuns(r.data.runs || []);
  };
  useEffect(() => { loadAll(); }, []);

  const run = async () => {
    if (!selected) return;
    setBusy(true); setOutput(null);
    let parsedInput = {};
    try { parsedInput = input ? JSON.parse(input) : {}; }
    catch { toast.error("Input must be valid JSON"); setBusy(false); return; }
    try {
      const { data } = await api.post("/agents/run", { agent_type: selected.agent_type, input: parsedInput });
      setOutput(data.result);
      toast.success(`${selected.display_name} completed`);
      loadAll();
    } catch (e) { toast.error("Agent execution failed"); }
    setBusy(false);
  };

  const defaultInputs = {
    prospect: { icp: "Heads of Operations at mid-market EU freight forwarders" },
    research: { company: "BluePort Supply Co", prospect_name: "Anders Berg", title: "Director of Logistics" },
    personalization: { prospect: { full_name: "Marco Conti", title: "VP Operations", company: "Velocity Freight Group", signals: ["fleet_expansion"] }, channel: "linkedin", step: "connect", tone: "consultative" },
    safety: {},
  };

  return (
    <DashboardLayout eyebrow="AI Agents" title="Autonomous workforce">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Agent grid */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid md:grid-cols-2 gap-0 grid-borders">
            {agents.map((a, i) => {
              const Icon = ICONS[a.agent_type] || Bot;
              const isActive = a.status === "active";
              return (
                <button
                  key={a.agent_type} data-testid={`agent-${a.agent_type}-card`}
                  onClick={() => { if (!isActive) return; setSelected(a); setInput(JSON.stringify(defaultInputs[a.agent_type] || {}, null, 2)); setOutput(null); }}
                  className={`p-5 text-left transition-colors ${isActive ? "hover:bg-muted/40 cursor-pointer" : "opacity-60 cursor-not-allowed"} ${selected?.agent_type === a.agent_type ? "bg-muted/50" : ""}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {isActive
                      ? <span className="text-[0.6rem] tracking-widest uppercase text-signal-500">Live · {a.runs_count} runs</span>
                      : <span className="text-[0.6rem] tracking-widest uppercase text-amber-accent">Phase 2</span>}
                  </div>
                  <div className="font-display font-semibold tracking-tight">{a.display_name}</div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{a.description}</p>
                </button>
              );
            })}
          </div>

          {/* Runner */}
          {selected && (
            <div className="data-card" data-testid="agent-runner">
              <div className="label-eyebrow mb-1">Run agent</div>
              <h3 className="font-display font-semibold text-lg">{selected.display_name}</h3>
              <p className="text-xs text-muted-foreground mb-4">{selected.description}</p>
              <Textarea data-testid="agent-input" rows={6} value={input} onChange={(e) => setInput(e.target.value)} className="font-mono text-xs" />
              <div className="mt-3 flex justify-end">
                <Button onClick={run} disabled={busy} data-testid="agent-run-btn" className="rounded-sm">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />{busy ? "Running..." : "Execute"}
                </Button>
              </div>
              {output && (
                <div className="mt-4">
                  <div className="label-eyebrow mb-2">Output</div>
                  <pre className="text-xs font-mono whitespace-pre-wrap bg-muted/30 border border-border rounded-sm p-4 max-h-[400px] overflow-y-auto" data-testid="agent-output">
                    {typeof output === "string" ? output : JSON.stringify(output, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Activity log */}
        <div className="data-card">
          <div className="label-eyebrow mb-2">Live activity</div>
          <h3 className="font-display font-semibold text-lg mb-4">Recent agent runs</h3>
          <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {runs.length === 0 && <li className="text-xs text-muted-foreground">No runs yet — pick an agent and execute.</li>}
            {runs.map((r, i) => (
              <li key={r.id || i} className="flex items-start gap-3 border-l-2 border-signal-500/40 pl-3 py-1" data-testid={`agent-activity-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium capitalize">{r.agent_type} Agent</div>
                  <div className="text-[0.7rem] text-muted-foreground">{relativeTime(r.created_at)} · {r.status}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
